import PageContainer from '@/common/components/PageContainer'
import DashboardListing from '@/modules/dashboard/components/Dashboard/components/DashboardListing'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import useQueryGetOverallAnalyticsCumulative from '@/modules/dashboard/api/hooks/useQueryGetOverallAnalyticsCumulative'
import useQueryGetOverallAnalyticsAggregate from '@/modules/dashboard/api/hooks/useQueryGetOverallAnalyticsAggregate'
import useQueryGetOnlineActiveUsers from '@/modules/dashboard/api/hooks/useQueryGetOnlineActiveUser'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { useEffect, useMemo } from 'react'
import { format, startOfDay, endOfDay } from 'date-fns'
import { useQueryState, parseAsString } from 'nuqs'
import OverallDepositWithdrawGraph from '@/modules/dashboard/components/Dashboard/components/OverallDepositWithdrawGraph'
import TransactionSummary from '@/modules/dashboard/components/TransactionSummary'
import { Button } from '@/common/ui/button'
import { Download } from 'lucide-react'
import { exportCardMetricsToCsv } from '@/modules/dashboard/utils/exportCsv'
import { useMutationExportTransactionSummaryCsv } from '@/modules/dashboard/api/hooks/useMutationExportTransactionSummaryCsv'
import type { GetOverallAnalyticsResponse } from '@/modules/dashboard/type'

function OverallAnalyticPage() {
	const { dateFilter, setDateFilter } = useDateFilter()
	const [dateRange, setDateRange] = useQueryState(
		'dateRange',
		parseAsString.withDefault(''),
	)
	const exportTransactionMutation = useMutationExportTransactionSummaryCsv()

	useEffect(() => {
		// Only set today's date if there are no dates AND dateRange is not 'all'
		if (!dateFilter.startDate && !dateFilter.endDate && dateRange !== 'all') {
			const today = new Date()
			setDateFilter({
				startDate: format(startOfDay(today), 'yyyy-MM-dd'),
				endDate: format(endOfDay(today), 'yyyy-MM-dd'),
			})
			setDateRange('today')
		}
	}, [
		dateFilter.startDate,
		dateFilter.endDate,
		dateRange,
		setDateFilter,
		setDateRange,
	])

	const payload = {
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
	}
	const { data: cumulativeData, isPending: isPendingCumulative } =
		useQueryGetOverallAnalyticsCumulative(payload)
	const { data: aggregateData, isPending: isPendingAggregate } =
		useQueryGetOverallAnalyticsAggregate(payload)

	const overallData = useMemo((): GetOverallAnalyticsResponse | undefined => {
		if (!cumulativeData || !aggregateData) return undefined
		return { ...cumulativeData, ...aggregateData }
	}, [cumulativeData, aggregateData])

	const isLoadingOverallData = isPendingCumulative || isPendingAggregate
	const { data: onlineUsersData, isLoading: isLoadingOnlineUsers } =
		useQueryGetOnlineActiveUsers()

	const handleExportCsv = async () => {
		// Export dashboard cards CSV
		if (overallData) {
			exportCardMetricsToCsv(
				overallData,
				dateFilter.startDate,
				dateFilter.endDate,
			)
		}

		// Export transaction summary CSV
		const startDate = dateFilter.startDate
			? `${dateFilter.startDate}T00:00:00.000Z`
			: undefined
		const endDate = dateFilter.endDate
			? `${dateFilter.endDate}T00:00:00.000Z`
			: undefined

		const transactionColumnMappings = {
			providerName: 'ผู้ให้บริการ',
			totalSuccessTransaction: 'จำนวนธุรกรรม',
			totalTurnOver: 'ยอดเทิร์น',
			totalTransferIn: 'ยอดโอนเข้า',
			totalTransferOut: 'ยอดโอนออก',
			totalBetAmount: 'ยอดเดิมพัน',
			totalSettleAmount: 'ยอดจ่าย',
		}

		await exportTransactionMutation.mutateAsync({
			startDate,
			endDate,
			filename: `transaction-summary_${
				dateFilter.startDate || 'all'
			}_to_${dateFilter.endDate || 'all'}`,
			columnMappings: transactionColumnMappings,
		})
	}

	return (
		<PageContainer>
			<div className="space-y-4">
				<div className="flex justify-end">
					<Button
						variant="outline"
						onClick={handleExportCsv}
						disabled={
							!overallData ||
							isLoadingOverallData ||
							exportTransactionMutation.isPending
						}
						className="flex items-center gap-2"
					>
						<Download className="h-4 w-4" />
						ดาวน์โหลด CSV
					</Button>
				</div>
				<DashboardListing
					data={overallData}
					isLoading={isLoadingOverallData}
					onlineUsersData={onlineUsersData}
					isLoadingOnlineUsers={isLoadingOnlineUsers}
				/>
				<OverallDepositWithdrawGraph
					startDate={dateFilter.startDate}
					endDate={dateFilter.endDate}
				/>
				<TransactionSummary />
			</div>
		</PageContainer>
	)
}

const GuardedOverallAnalyticPage = withUserPermission(OverallAnalyticPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_MARKETING],
})

export default GuardedOverallAnalyticPage
